'use client';

import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import { cva } from 'class-variance-authority';
import { cn } from '@/utils/cn';
type TabsProps = TabsPrimitive.Root.Props;
export function Tabs(props) {
  const $ = _c(10);
  let className;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      className,
      orientation: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
    $[3] = t0;
  } else {
    className = $[1];
    rest = $[2];
    t0 = $[3];
  }
  const orientation = t0 === undefined ? "horizontal" : t0;
  let t1;
  if ($[4] !== className) {
    t1 = cn("group/tabs flex gap-2 data-[orientation=horizontal]:flex-col", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== orientation || $[7] !== rest || $[8] !== t1) {
    t2 = <TabsPrimitive.Root data-slot="tabs" data-orientation={orientation} className={t1} {...rest} />;
    $[6] = orientation;
    $[7] = rest;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export const tabsListVariants = cva('group/tabs-list inline-flex w-fit items-center justify-center rounded-4xl p-0.75 text-muted-foreground group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col group-data-horizontal/tabs:h-9 group-data-vertical/tabs:rounded-2xl data-[variant=line]:rounded-none', {
  variants: {
    variant: {
      default: 'bg-muted',
      line: 'gap-1 bg-transparent'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
type TabsListProps = TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>;
export function TabsList(props) {
  const $ = _c(11);
  let className;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      className,
      variant: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
    $[3] = t0;
  } else {
    className = $[1];
    rest = $[2];
    t0 = $[3];
  }
  const variant = t0 === undefined ? "default" : t0;
  let t1;
  if ($[4] !== className || $[5] !== variant) {
    t1 = cn(tabsListVariants({
      variant
    }), className);
    $[4] = className;
    $[5] = variant;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== rest || $[8] !== t1 || $[9] !== variant) {
    t2 = <TabsPrimitive.List data-slot="tabs-list" data-variant={variant} className={t1} {...rest} />;
    $[7] = rest;
    $[8] = t1;
    $[9] = variant;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
type TabsTriggerProps = TabsPrimitive.Tab.Props;
export function TabsTrigger(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-xl border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap text-foreground/60 transition-all group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start group-data-[variant=line]/tabs-list:bg-transparent group-data-vertical/tabs:px-2.5 group-data-vertical/tabs:py-1.5 after:absolute after:bg-foreground after:opacity-0 after:transition-opacity group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:-bottom-1.25 group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5 hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 dark:text-muted-foreground dark:hover:text-foreground data-active:bg-background data-active:text-foreground group-data-[variant=line]/tabs-list:data-active:bg-transparent group-data-[variant=line]/tabs-list:data-active:after:opacity-100 dark:data-active:border-input dark:data-active:bg-input/30 dark:data-active:text-foreground dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <TabsPrimitive.Tab data-slot="tabs-trigger" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type TabsContentProps = TabsPrimitive.Panel.Props;
export function TabsContent(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("flex-1 text-sm outline-none", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <TabsPrimitive.Panel data-slot="tabs-content" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}